import React, { useState } from 'react';
import styled from 'styled-components';
import Panel from './Panel';
import PeriodSelector from './PeriodSelector';
import useMonitoringData from '../hooks/useMonitoring';
import {
  fetchLambdaStatus,
  fetchLambdaErrors,
  fetchPaymentActivity,
} from '../api/monitoringApi';

const Banner = styled.div`
  border-radius: 10px; padding: 14px 16px; margin-bottom: 14px;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  background: ${p => p.$danger ? '#fef2f2' : '#ecfdf5'};
  border: 1px solid ${p => p.$danger ? '#fecaca' : '#a7f3d0'};
  color: ${p => p.$danger ? '#991b1b' : '#065f46'};
`;
const BannerIcon = styled.div`
  font-size: 20px; width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: ${p => p.$danger ? '#fee2e2' : '#d1fae5'};
  color: ${p => p.$danger ? '#b91c1c' : '#047857'};
`;
const BannerBody = styled.div`flex: 1;`;
const BannerTitle = styled.div`font-size: 14px; font-weight: 700;`;
const BannerSub = styled.div`font-size: 12px; color: inherit; opacity: 0.75; margin-top: 2px;`;

const StatusRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 14px;
  padding: 10px 12px; background: #f9fafb; border-radius: 8px; font-size: 12px; color: #374151;
`;
const StatusItem = styled.div`
  display: flex; align-items: center; gap: 6px;
  strong { color: #111827; }
`;
const StatusDot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${p => {
    switch (p.$status) {
      case 'active': return '#10b981';
      case 'idle': return '#f59e0b';
      case 'no_logs': return '#9ca3af';
      case 'not_found': return '#ef4444';
      case 'error': return '#dc2626';
      default: return '#d1d5db';
    }
  }};
`;

const ErrorList = styled.div`
  max-height: 340px; overflow-y: auto;
  border: 1px solid #fecaca; border-radius: 8px;
  background: #fff;
`;
const ErrorItem = styled.div`
  padding: 10px 12px; border-bottom: 1px solid #fee2e2;
  &:last-child { border-bottom: none; }
`;
const ErrorHeader = styled.div`
  display: flex; align-items: center; gap: 8px; cursor: pointer;
`;
const CategoryBadge = styled.span`
  font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 4px; white-space: nowrap;
  background: ${p => {
    switch (p.$cat) {
      case 'payment_failure': return '#fee2e2';
      case 'db_error': return '#ffedd5';
      default: return '#e5e7eb';
    }
  }};
  color: ${p => {
    switch (p.$cat) {
      case 'payment_failure': return '#b91c1c';
      case 'db_error': return '#c2410c';
      default: return '#4b5563';
    }
  }};
`;
const ErrorTime = styled.span`font-size: 11px; color: #6b7280; white-space: nowrap;`;
const ErrorSnippet = styled.div`
  font-size: 12px; color: #374151; margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const ErrorDetail = styled.pre`
  margin: 8px 0 0; padding: 8px 10px; background: #fafafa; border-radius: 6px;
  font-size: 11px; color: #1f2937; white-space: pre-wrap; word-break: break-word;
  max-height: 200px; overflow-y: auto;
`;
const ToggleBtn = styled.button`
  background: none; border: none; color: #6b7280; font-size: 11px; cursor: pointer;
  margin-left: auto;
  &:hover { color: #111827; }
`;

const ActivitySection = styled.div`
  margin-top: 18px; padding: 12px 14px; background: #f9fafb; border-radius: 10px;
`;
const ActivityTitle = styled.div`
  font-size: 12px; font-weight: 700; color: #6b7280; margin-bottom: 10px;
  text-transform: uppercase; letter-spacing: 0.5px;
`;
const ActivityGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
  @media (max-width: 700px) { grid-template-columns: repeat(2, 1fr); }
`;
const ActivityCard = styled.div`
  background: #fff; border-radius: 8px; padding: 10px 12px;
  border-left: 3px solid ${p => p.$accent || '#d1d5db'};
  .label { font-size: 11px; color: #6b7280; }
  .val { font-size: 20px; font-weight: 700; color: #111827; margin-top: 2px; }
`;

const HOUR_OPTIONS = [
  { label: '6시간', value: 6 },
  { label: '24시간', value: 24 },
  { label: '3일', value: 72 },
  { label: '7일', value: 168 },
];

const CATEGORY_LABEL = {
  payment_failure: '결제 실패',
  db_error: 'DB 오류',
  unknown: '알 수 없음',
};

const STATUS_LABEL = {
  active: 'Active',
  idle: 'Idle',
  no_logs: '로그 없음',
  not_found: '로그 그룹 없음',
  error: '조회 실패',
};

function fmtTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function firstLine(msg) {
  if (!msg) return '';
  return msg.split('\n')[0].slice(0, 140);
}

export default function LambdaLogPanel({ refreshKey }) {
  const [hours, setHours] = useState(24);
  const [expanded, setExpanded] = useState({});

  const status = useMonitoringData(fetchLambdaStatus, [refreshKey]);
  const errors = useMonitoringData(() => fetchLambdaErrors(hours, 50), [hours, refreshKey]);
  const activity = useMonitoringData(() => fetchPaymentActivity(hours), [hours, refreshKey]);

  const isLoading = status.isLoading || errors.isLoading || activity.isLoading;
  const error = status.error || errors.error || activity.error;

  const errList = errors.data || [];
  const errCount = errList.length;
  const hasError = errCount > 0;

  const toggle = (i) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }));

  const hourLabel = HOUR_OPTIONS.find(o => o.value === hours)?.label || `${hours}시간`;

  return (
    <Panel
      title="람다 로그 현황 (결제 재시도)"
      isLoading={isLoading}
      error={error}
      actions={
        <PeriodSelector
          value={hours}
          onChange={setHours}
          options={HOUR_OPTIONS.map(o => o.value)}
          renderLabel={(v) => HOUR_OPTIONS.find(o => o.value === v)?.label || `${v}h`}
        />
      }
    >
      {/* 상단 배너 — 에러 유무에 따라 전환 */}
      {hasError ? (
        <Banner $danger>
          <BannerIcon $danger>⚠</BannerIcon>
          <BannerBody>
            <BannerTitle>최근 {hourLabel} 에러 {errCount}건 감지</BannerTitle>
            <BannerSub>아래 에러 로그를 확인해 원인/영향받은 구독을 파악하세요.</BannerSub>
          </BannerBody>
        </Banner>
      ) : (
        <Banner>
          <BannerIcon>✓</BannerIcon>
          <BannerBody>
            <BannerTitle>최근 {hourLabel} 에러 없음 · 정상 동작 중</BannerTitle>
            <BannerSub>람다 결제 재시도에서 오류가 발견되지 않았습니다.</BannerSub>
          </BannerBody>
        </Banner>
      )}

      {/* 람다 상태 요약 */}
      <StatusRow>
        {(status.data || []).length === 0 && (
          <StatusItem style={{ color: '#9ca3af' }}>
            <StatusDot $status="no_logs" />
            <span>로컬 환경에서는 CloudWatch 상태를 조회하지 않습니다</span>
          </StatusItem>
        )}
        {(status.data || []).map((fn) => (
          <StatusItem key={fn.name}>
            <StatusDot $status={fn.status} />
            <strong>{fn.name}</strong>
            <span style={{ color: '#6b7280' }}>· {STATUS_LABEL[fn.status] || fn.status}</span>
            {fn.last_event && <span style={{ color: '#9ca3af' }}>· 마지막 실행 {fmtTime(fn.last_event)}</span>}
          </StatusItem>
        ))}
      </StatusRow>

      {/* 에러 리스트 (있을 때만) */}
      {hasError && (
        <ErrorList>
          {errList.map((e, i) => {
            const open = !!expanded[i];
            return (
              <ErrorItem key={i}>
                <ErrorHeader onClick={() => toggle(i)}>
                  <CategoryBadge $cat={e.category}>{CATEGORY_LABEL[e.category] || e.category}</CategoryBadge>
                  <ErrorTime>{fmtTime(e.timestamp)}</ErrorTime>
                  <ErrorSnippet>{firstLine(e.message)}</ErrorSnippet>
                  <ToggleBtn>{open ? '접기 ▲' : '전체 ▼'}</ToggleBtn>
                </ErrorHeader>
                {open && (
                  <>
                    <ErrorDetail>{e.message}</ErrorDetail>
                    {e.log_stream && (
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                        stream: {e.log_stream}
                      </div>
                    )}
                  </>
                )}
              </ErrorItem>
            );
          })}
        </ErrorList>
      )}

      {/* 결제 처리 현황 (항상 노출) */}
      {activity.data && (
        <ActivitySection>
          <ActivityTitle>결제 처리 현황 · 최근 {hourLabel}</ActivityTitle>
          <ActivityGrid>
            <ActivityCard $accent="#10b981">
              <div className="label">정상 결제</div>
              <div className="val">{activity.data.success}</div>
            </ActivityCard>
            <ActivityCard $accent="#6b7280">
              <div className="label">취소</div>
              <div className="val">{activity.data.cancel}</div>
            </ActivityCard>
            <ActivityCard $accent="#f59e0b">
              <div className="label">환불</div>
              <div className="val">{activity.data.refund}</div>
            </ActivityCard>
            <ActivityCard $accent="#3b82f6">
              <div className="label">재결제 감지</div>
              <div className="val">{activity.data.rebill}</div>
            </ActivityCard>
          </ActivityGrid>
          {activity.data.last_success_at && (
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 10 }}>
              마지막 정상 결제: {fmtTime(activity.data.last_success_at)}
            </div>
          )}
        </ActivitySection>
      )}
    </Panel>
  );
}
